import { useElementShown } from '@hh.ru/analytics-js';
import assessmentPracticeButtonClick from '@hh.ru/analytics-js-events/build/career_platform/assessment_common/assessment_practice_button_click';
import assessmentCopyLinkButtonClick from '@hh.ru/analytics-js-events/build/career_platform/assessment_landing/assessment_copy_link_button_click';
import assessmentCopyLinkElementShown from '@hh.ru/analytics-js-events/build/career_platform/assessment_landing/assessment_copy_link_element_shown';
import assessmentPracticeElementShown from '@hh.ru/analytics-js-events/build/career_platform/assessment_landing/assessment_practice_element_shown';
import { translation, TranslatedComponent } from '@hh.ru/front-static-app';
import { Button, GridLayout, useBreakpoint } from '@hh.ru/magritte-ui';
import { SquaresIntersectedSize24 } from '@hh.ru/magritte-ui-icon/icon';

import useQueryParamAsState from 'src/hooks/useQueryParamAsState';

import AlertOnlyDesktop from 'src/pages/CertLanding/components/SectionScreens/components/AlertOnlyDesktop';
import ModalStartPractice from 'src/pages/CertLanding/components/SectionScreens/components/ModalStartPractice';
import SectionScreensItem from 'src/pages/CertLanding/components/SectionScreens/components/SectionScreensItem';

import styles from './styles.less';

const TrlKeys = {
    1: {
        title: 'career-platform.page-cert-landing.section.screens.item.1.title',
        description: 'career-platform.page-cert-landing.section.screens.item.1.description',
    },
    2: {
        title: 'career-platform.page-cert-landing.section.screens.item.2.title',
        description: 'career-platform.page-cert-landing.section.screens.item.2.description',
    },
    3: {
        title: 'career-platform.page-cert-landing.section.screens.item.3.title',
        description: 'career-platform.page-cert-landing.section.screens.item.3.description',
    },
    4: {
        title: 'career-platform.page-cert-landing.section.screens.item.4.title',
        description: 'career-platform.page-cert-landing.section.screens.item.4.description',
    },
    5: {
        mobile: {
            title: 'career-platform.page-cert-landing.section.screens.item.5.mobile.title',
            description: 'career-platform.page-cert-landing.section.screens.item.5.mobile.description',
            button: 'career-platform.page-cert-landing.section.screens.item.5.mobile.button',
        },
        web: {
            title: 'career-platform.page-cert-landing.section.screens.item.5.web.title',
            description: 'career-platform.page-cert-landing.section.screens.item.5.web.description',
            button: 'career-platform.page-cert-landing.section.screens.item.5.web.button',
        },
    },
};

const QUERY_MODAL_KEY = 'modal';
const QUERY_MODAL_VALUE = 'start-practice';

const SectionScreens: TranslatedComponent = ({ trls }) => {
    const { isMobile } = useBreakpoint();
    const [modalQueryValue, setModalQueryValue] = useQueryParamAsState(QUERY_MODAL_KEY);

    const copyLinkEditorRef = useElementShown(assessmentCopyLinkElementShown);
    const practiceRef = useElementShown(assessmentPracticeElementShown);

    const handleClickCopyTrainLink = async () => {
        assessmentCopyLinkButtonClick();
        try {
            const { protocol, host, pathname } = window.location;
            const url = `${protocol}//${host}${pathname}?${QUERY_MODAL_KEY}=${QUERY_MODAL_VALUE}`;
            await navigator.clipboard.writeText(url);
        } catch {} // eslint-disable-line no-empty
    };

    const handleClickGoTrain = () => {
        assessmentPracticeButtonClick();
        setModalQueryValue(QUERY_MODAL_VALUE);
    };

    const handleCloseModal = () => setModalQueryValue(null);

    return (
        <>
            <GridLayout>
                <section className={styles.container}>
                    <SectionScreensItem
                        dash="green"
                        title={trls[TrlKeys['1'].title]}
                        description={trls[TrlKeys['1'].description]}
                        imageIdx={1}
                    />
                    <SectionScreensItem
                        dash="blue"
                        title={trls[TrlKeys['2'].title]}
                        description={trls[TrlKeys['2'].description]}
                        imageIdx={2}
                    />
                    <SectionScreensItem
                        dash="sky"
                        title={trls[TrlKeys['3'].title]}
                        description={trls[TrlKeys['3'].description]}
                        imageIdx={3}
                    />
                    <SectionScreensItem
                        dash="violet"
                        title={trls[TrlKeys['4'].title]}
                        description={trls[TrlKeys['4'].description]}
                        imageIdx={4}
                    />
                    {isMobile && (
                        <SectionScreensItem
                            dash={null}
                            title={trls[TrlKeys['5'].mobile.title]}
                            description={trls[TrlKeys['5'].mobile.description]}
                            action={
                                <Button
                                    mode="primary"
                                    size="large"
                                    icon={<SquaresIntersectedSize24 />}
                                    stretched
                                    onClick={handleClickCopyTrainLink}
                                >
                                    {trls[TrlKeys['5'].mobile.button]}
                                </Button>
                            }
                            imageIdx={5}
                            ref={copyLinkEditorRef}
                        />
                    )}
                    {!isMobile && (
                        <SectionScreensItem
                            dash={null}
                            title={trls[TrlKeys['5'].web.title]}
                            description={trls[TrlKeys['5'].web.description]}
                            action={
                                <Button mode="primary" size="large" onClick={handleClickGoTrain}>
                                    {trls[TrlKeys['5'].web.button]}
                                </Button>
                            }
                            imageIdx={5}
                            ref={practiceRef}
                        />
                    )}
                </section>
            </GridLayout>
            {isMobile && <AlertOnlyDesktop visible={!!modalQueryValue} onClose={handleCloseModal} />}
            {!isMobile && <ModalStartPractice visible={!!modalQueryValue} onClose={handleCloseModal} />}
        </>
    );
};

export default translation(SectionScreens);
